import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { ExternalLink, Pencil, Printer, ScanLine, Star } from "lucide-react";
import { money, calcCommission, effectiveRate, calcAgentCommission } from "../lib/finance";
import { API } from "../lib/apiClient";
import { Modal, Field, Select, Empty } from "./common";
import { PROVINCES, districtsOf } from "../lib/trAddress";
import { IdOcrCompare } from "./IdOcrCompare";
import { CustomerInfoGrid } from "./CustomerCard";
import { PrintOptionsModal } from "./PrintOptionsModal";

export function CustomerModal({ initial, onSave, onClose }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(() => ({
    id: initial?.id || "",
    name: initial?.name || "",
    type: initial?.type || "Bireysel",
    phone: initial?.phone || "",
    email: initial?.email || "",
    taxNo: initial?.taxNo || "",
    city: initial?.city || "",
    district: initial?.district || "",
    address: initial?.address || "",
    birthDate: initial?.birthDate || "",
    note: initial?.note || "",
    commissionPerson: initial?.commissionPerson || "",
    commissionRate: initial?.commissionRate != null ? String(initial.commissionRate) : "",
  }));
  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const [idOcr, setIdOcr] = useState(null);
  const [idBusy, setIdBusy] = useState(false);
  const readId = async (file) => {
    if (!file) return;
    setIdBusy(true); setIdOcr(null);
    try {
      const fd = new FormData(); fd.append("file", file, file.name);
      const { data } = await axios.post(`${API}/customers/ocr-kimlik`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setIdOcr({ ...data, at: Date.now() });
    } catch (e) { window.alert(e?.response?.data?.detail || "Kimlik okunamadı"); } finally { setIdBusy(false); }
  };
  return (
    <Modal title={isEdit ? `${initial.name} · düzenle` : "Yeni müşteri ekle"} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); onSave(form); }}>
        <div className="id-fill-bar">
          <label className="outline-button" data-testid="customer-id-fill-button">
            <ScanLine size={14} /> {idBusy ? "Kimlik okunuyor…" : "Kimlikten doldur"}
            <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} disabled={idBusy} onChange={(e) => { readId(e.target.files?.[0]); e.target.value = ""; }} data-testid="customer-id-fill-input" />
          </label>
          <small>Kimlik fotoğrafından ad, T.C. no ve doğum tarihi okunur</small>
        </div>
        {idOcr && <IdOcrCompare key={idOcr.at} ocr={idOcr} current={form} applyLabel="Forma uygula" doneText="Forma uygulandı — kaydetmeyi unutmayın" testPrefix="modal-id-ocr" onApply={async (fields) => setForm((f) => ({ ...f, ...fields }))} />}
        <div className="form-grid">
          <Field label="Ad soyad / ticari unvan" name="name" value={form.name} onChange={change} required />
          <Select label="Müşteri türü" name="type" value={form.type} onChange={change} options={["Bireysel", "Acente", "Galeri"]} />
          <Field label="Telefon" name="phone" value={form.phone} onChange={change} />
          <Field label="E-posta" name="email" type="email" value={form.email} onChange={change} />
          <Field label="T.C. / Vergi no" name="taxNo" value={form.taxNo} onChange={change} />
          <Field label="Doğum tarihi" name="birthDate" type="date" value={form.birthDate} onChange={change} />
          <Field label="Komisyon verilen kişi" name="commissionPerson" value={form.commissionPerson} onChange={change} />
          <Field label="Verilen komisyon (%)" name="commissionRate" type="number" value={form.commissionRate} onChange={change} />
        </div>
        <div className="form-grid">
          <label className="field">
            <span>İl</span>
            <select name="city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value, district: "" })} data-testid="customer-city-select">
              <option value="">İl seçin</option>
              {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label className="field">
            <span>İlçe</span>
            <select name="district" value={form.district} onChange={change} disabled={!form.city} data-testid="customer-district-select">
              <option value="">{form.city ? "İlçe seçin" : "Önce il seçin"}</option>
              {districtsOf(form.city).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
        </div>
        <label className="field full">
          <span>Açık adres</span>
          <textarea name="address" rows={2} value={form.address} onChange={change} placeholder="Mahalle, cadde/sokak, bina no, daire" data-testid="customer-address-input" />
        </label>
        <label className="field full">
          <span>Notlar</span>
          <textarea name="note" data-testid="customer-note-input" value={form.note} onChange={change} />
        </label>
        <div className="modal-actions">
          <button type="button" className="outline-button" data-testid="customer-modal-cancel" onClick={onClose}>Vazgeç</button>
          <button className="primary-button" data-testid="customer-modal-save">{isEdit ? "Değişiklikleri kaydet" : "Müşteriyi kaydet"}</button>
        </div>
      </form>
    </Modal>
  );
}


export function CustomerDetail({ customer, policies, branches, settings, onEdit, onClose, onToggleFavorite, onOpenPortal }) {
  const rows = policies.filter((p) => p.customer === customer.name);
  const totalPersonal = rows.reduce((s, p) => s + calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, p.status === "iptal" ? p.cancellationRefund : null).personal, 0);
  const custRate = Number(customer?.commissionRate) || 0;
  const totalAgent = custRate > 0 ? rows.reduce((s, p) => s + calcAgentCommission(p, [customer], branches, settings).agent, 0) : 0;
  const [printOpen, setPrintOpen] = useState(false);
  return (
    <>
    <Modal title="Müşteri özeti" onClose={onClose}>
      <div className="detail-header">
        <div className="avatar large">{customer.name.split(" ").map((x) => x[0]).join("").slice(0, 2)}</div>
        <div style={{ flex: 1 }}><h3>{customer.name} {onToggleFavorite && <button data-testid="customer-favorite-toggle" onClick={() => onToggleFavorite(customer)} title={customer.favorite ? "Favoriden çıkar" : "Favorilere ekle"} style={{ background: "none", border: 0, cursor: "pointer", padding: "0 4px", verticalAlign: "middle" }}><Star size={18} fill={customer.favorite ? "#f59e0b" : "none"} color={customer.favorite ? "#f59e0b" : "#94a3b8"} /></button>}</h3><p>{customer.type} · {customer.phone}{custRate > 0 ? ` · Komisyon %${custRate}` : ""}</p></div>
        <button className="ghost-button" data-testid="customer-detail-print" onClick={() => setPrintOpen(true)} title="Yazdır"><Printer size={14} /> Yazdır</button>
        {onOpenPortal && <button className="ghost-button" data-testid="customer-portal-button" onClick={() => onOpenPortal(customer)} title="Müşteri portalı linki"><ExternalLink size={14} /> Portal linki</button>}
        {onEdit && <button className="ghost-button" data-testid="customer-detail-edit" onClick={onEdit}><Pencil size={14} /> Düzenle</button>}
      </div>
      <CustomerInfoGrid c={customer} />
      <div className="detail-stats">
        <div><span>Poliçe</span><strong>{rows.length}</strong></div>
        <div><span>Toplam brüt prim</span><strong>{money(rows.reduce((s, p) => s + Number(p.premium || 0), 0))}</strong></div>
        <div><span>Toplam kişisel hak ediş</span><strong>{money(totalPersonal)}</strong></div>
        {custRate > 0 && <div><span>Acenteye ödenen (%{custRate})</span><strong style={{ color: "#be123c" }}>{money(totalAgent)}</strong></div>}
        {custRate > 0 && <div><span>Net kazancınız</span><strong style={{ color: "#059669" }}>{money(totalPersonal - totalAgent)}</strong></div>}
      </div>
      <div className="detail-list">
        {rows.map((p) => (
          <div key={p.id}>
            <strong>{p.policeNo}</strong>
            <span>{p.branch} · Bitiş: {p.end} · Brüt: {money(p.premium)}</span>
          </div>
        ))}
        {!rows.length && <Empty text="Bu müşteride poliçe yok" />}
      </div>
    </Modal>
    {printOpen && <PrintOptionsModal customer={customer} policies={policies} branches={branches} settings={settings} onClose={() => setPrintOpen(false)} />}
    </>
  );
}


export function CustomerPortalLinkModal({ customer, onClose }) {
  const [tok, setTok] = useState(customer.portalToken || "");
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const qrRef = useRef(null);
  useEffect(() => {
    if (!tok) {
      (async () => {
        try { const { data } = await axios.get(`${API}/customers/${customer.id}/portal-token`); setTok(data.portalToken); }
        catch {}
      })();
    }
  }, [customer.id, tok]);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = tok ? `${origin}/portal/${tok}` : "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { window.prompt("Kopyalayın:", url); }
  };
  const regen = async () => {
    if (!window.confirm("Yeni link üretilirse eski link çalışmaz. Devam?")) return;
    setBusy(true);
    try { const { data } = await axios.post(`${API}/customers/${customer.id}/portal-token/regenerate`); setTok(data.portalToken); }
    catch { window.alert("Yenileme başarısız."); } finally { setBusy(false); }
  };
  return (
    <Modal title={`${customer.name} — Müşteri Portalı`} onClose={onClose}>
      <div className="modal-body" data-testid="portal-link-modal">
        <p className="muted small">Müşteriyle paylaşabileceğiniz özel bir sayfa oluşturdunuz. Müşteri bu linkten poliçelerini ve yaklaşan yenilemelerini görebilir. Komisyon/net prim bilgisi gizlidir.</p>
        {tok && (
          <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap", padding: "14px 0" }}>
            <div ref={qrRef} data-testid="portal-link-qr" style={{ padding: 10, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, lineHeight: 0 }}>
              <QRCodeSVG value={url} size={140} level="M" bgColor="#ffffff" fgColor="#0f172a" />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <strong style={{ fontSize: 13, display: "block" }}>Paylaşım Linki</strong>
              <input readOnly value={url} data-testid="portal-link-url" style={{ marginTop: 6, width: "100%" }} />
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                <button type="button" className="primary-button small" data-testid="portal-link-copy" onClick={copy}>{copied ? "Kopyalandı ✓" : "Kopyala"}</button>
                <button type="button" className="outline-button small danger" data-testid="portal-link-regen" onClick={regen} disabled={busy}>Linki yenile</button>
              </div>
            </div>
          </div>
        )}
        <div className="modal-actions">
          <button type="button" className="primary-button" data-testid="portal-link-close" onClick={onClose}>Tamam</button>
        </div>
      </div>
    </Modal>
  );
}
